import React, { useState } from 'react';
import { Box, Typography, Avatar, CircularProgress, Link } from '@mui/material';
import PushPinIcon from '@mui/icons-material/PushPin';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import type { OptimisticMessage } from '../../contexts/CommunityContext';
import type { CommunityMessage } from '../../services/communityService';
import {
  COMMUNITY_SUPPORT_TEAM_LABEL,
  isCommunityStaffActorId,
} from '../../utils/communitySenderDisplay';
import { roleLabel, type CommunityRole } from '../../utils/communityPermissions';
import { extractCommunityInviteFromText } from '../../utils/communityInviteUrl';
import { MentionRenderer } from '../../utils/mentionRenderer';
import { avatarColor, formatMessageTime, getInitials } from './chatHelpers';
import { CommunityMediaLightbox } from './CommunityMediaLightbox';
import { CommunityVoiceNotePlayer } from './CommunityVoiceNotePlayer';
import { DocumentMessageCard } from './DocumentMessageCard';
import { LinkPreviewCard } from './LinkPreviewCard';

export interface CommunityMessageBubbleProps {
  message: OptimisticMessage;
  isOwn: boolean;
  showSender: boolean;
  senderDisplayName?: string;
  senderRole?: CommunityRole;
  isSelected?: boolean;
  onClick?: () => void;
  onReplyPreviewClick?: (messageId: string) => void;
  replyToMessage?: CommunityMessage | null;
}

function replyPreviewText(msg: CommunityMessage | null | undefined): string {
  if (!msg) return 'Message';
  if (msg.message_type === 'deleted' || msg.deleted) return 'Message deleted';
  if (msg.link_preview?.title) return msg.link_preview.title;
  if (msg.content?.trim()) return msg.content.trim();
  if (msg.forwarded_from?.content_snippet) return msg.forwarded_from.content_snippet;
  switch (msg.message_type) {
    case 'image': return '📷 Photo';
    case 'video': return '🎥 Video';
    case 'audio': return '🎙️ Audio';
    case 'document': return '📎 Document';
    case 'location': return '📍 Location';
    default: return 'Message';
  }
}

export const CommunityMessageBubble: React.FC<CommunityMessageBubbleProps> = ({
  message,
  isOwn,
  showSender,
  senderDisplayName,
  senderRole,
  isSelected = false,
  onClick,
  onReplyPreviewClick,
  replyToMessage,
}) => {
  const replyTargetId =
    message.reply_to_message_id ?? replyToMessage?.community_message_id ?? null;

  const handleReplyPreviewClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (replyTargetId) onReplyPreviewClick?.(replyTargetId);
  };
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const displayName =
    senderDisplayName?.trim() ||
    (isCommunityStaffActorId(message.sender_client_id)
      ? COMMUNITY_SUPPORT_TEAM_LABEL
      : undefined) ||
    (message.sender_name_cached !== 'Member' ? message.sender_name_cached : undefined) ||
    message.sender_client_id ||
    'Unknown';
  if (message.message_type === 'system_event') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 0.5 }}>
        <Typography
          sx={{
            fontSize: '0.75rem',
            color: '#9aa0a6',
            backgroundColor: '#f1f3f4',
            borderRadius: 10,
            px: 1.5,
            py: 0.25,
          }}
        >
          {message.content}
        </Typography>
      </Box>
    );
  }

  const isDeleted = message.message_type === 'deleted' || message.deleted;

  const renderMessageBody = () => {
    if (isDeleted) {
      return (
        <Typography sx={{ fontSize: '0.85rem', color: '#9aa0a6', fontStyle: 'italic' }}>
          This message was deleted
        </Typography>
      );
    }

    if (message.message_type === 'text') {
      const content = message.content || '';
      const communityInvite = extractCommunityInviteFromText(content);

      return (
        <>
          <Box sx={{ opacity: message._optimistic ? 0.7 : 1 }}>
            <MentionRenderer content={content} />
          </Box>
          {message.edited_at && (
            <Typography component="span" sx={{ fontSize: '0.7rem', color: '#9aa0a6' }}>
              (edited)
            </Typography>
          )}
          {!communityInvite && message.link_preview && (
            <LinkPreviewCard
              preview={message.link_preview}
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </>
      );
    }

    if (message.message_type === 'location') {
      const lat = message.media?.latitude;
      const lng = message.media?.longitude;
      const label = message.content || message.media?.location_label || 'Shared location';
      const mapsUrl =
        typeof lat === 'number' && typeof lng === 'number'
          ? `https://maps.google.com/?q=${lat},${lng}`
          : undefined;
      return (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <LocationOnIcon sx={{ color: '#d93025', fontSize: 20 }} />
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#0f0f0f' }}>
              {label}
            </Typography>
          </Box>
          {mapsUrl && (
            <Link
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              sx={{ fontSize: '0.78rem' }}
            >
              Open in Maps
            </Link>
          )}
        </Box>
      );
    }

    if (message.message_type === 'image' && message.media?.blob_url) {
      return (
        <>
          <Box
            component="img"
            src={message.media.thumb_url || message.media.blob_url}
            alt={message.media.filename || 'Image'}
            onClick={(e) => {
              e.stopPropagation();
              setLightboxOpen(true);
            }}
            sx={{
              maxWidth: '100%',
              maxHeight: 280,
              borderRadius: 1,
              display: 'block',
              cursor: 'pointer',
            }}
          />
          {message.content && (
            <Typography sx={{ fontSize: '0.85rem', color: '#0f0f0f', mt: 0.5 }}>
              {message.content}
            </Typography>
          )}
          <CommunityMediaLightbox
            open={lightboxOpen}
            onClose={() => setLightboxOpen(false)}
            mediaUrl={message.media.blob_url}
            mediaType="image"
          />
        </>
      );
    }

    if (message.message_type === 'video' && message.media?.blob_url) {
      return (
        <>
          <Box
            component="video"
            src={message.media.blob_url}
            poster={message.media.thumb_url}
            controls
            onClick={(e) => e.stopPropagation()}
            sx={{ maxWidth: '100%', maxHeight: 280, borderRadius: 1, display: 'block' }}
          />
          {message.content && (
            <Typography sx={{ fontSize: '0.85rem', color: '#0f0f0f', mt: 0.5 }}>
              {message.content}
            </Typography>
          )}
        </>
      );
    }

    if (message.message_type === 'audio') {
      const audioUrl = message.media?.blob_url;
      const seed = message.community_message_id || audioUrl || message.media?.filename || 'audio';
      return (
        <CommunityVoiceNotePlayer
          src={audioUrl}
          durationMs={message.media?.duration_ms}
          isOwn={isOwn}
          senderName={displayName}
          senderAvatar={message.sender_avatar_cached}
          senderId={message.sender_client_id}
          seed={seed}
          onClick={(e) => e.stopPropagation()}
        />
      );
    }

    if (message.message_type === 'document') {
      const url = message.media?.blob_url;
      const name = message.media?.filename || message.content || 'Document';
      return (
        <DocumentMessageCard
          media={message.media ?? { filename: name }}
          filename={name}
          href={url}
          onClick={(e) => e.stopPropagation()}
        />
      );
    }

    if (message.media?.blob_url) {
      return (
        <Typography sx={{ fontSize: '0.85rem', color: '#0f0f0f' }}>
          {message.content || `📎 ${message.media.filename || message.message_type}`}
        </Typography>
      );
    }

    return (
      <Typography sx={{ fontSize: '0.88rem', color: '#0f0f0f' }}>{message.content}</Typography>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isOwn ? 'flex-end' : 'flex-start',
        mb: 0.75,
        px: 2,
        position: 'relative',
        backgroundColor: isSelected ? 'rgba(15, 157, 88, 0.12)' : 'transparent',
        transition: 'background-color 0.15s',
      }}
    >
      {!isOwn && (
        <Avatar
          src={message.sender_avatar_cached}
          sx={{
            width: 28,
            height: 28,
            bgcolor: avatarColor(message.sender_client_id ?? displayName),
            fontSize: '0.7rem',
            mr: 1,
            mt: 0.25,
            flexShrink: 0,
          }}
        >
          {getInitials(displayName)}
        </Avatar>
      )}

      <Box sx={{ maxWidth: '70%' }}>
        {showSender && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              mb: 0.25,
              ml: isOwn ? 0 : 0.5,
              mr: isOwn ? 0.5 : 0,
              justifyContent: isOwn ? 'flex-end' : 'flex-start',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.72rem',
                fontWeight: 600,
                color: avatarColor(message.sender_client_id ?? displayName),
              }}
            >
              {displayName}
            </Typography>
            {senderRole && (
              <Typography
                sx={{
                  fontSize: '0.65rem',
                  fontWeight: 500,
                  color: '#9aa0a6',
                  textTransform: 'uppercase',
                  letterSpacing: '0.02em',
                }}
              >
                {roleLabel(senderRole)}
              </Typography>
            )}
          </Box>
        )}

        <Box
          sx={{
            backgroundColor: isOwn ? '#d9fdd3' : '#fff',
            borderRadius: isOwn ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
            px: 1.5,
            py: 0.75,
            boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
            border: isOwn ? 'none' : '1px solid #e8eaed',
            position: 'relative',
          }}
        >
          {message.forwarded_from && (
            <Typography sx={{ fontSize: '0.68rem', color: '#9aa0a6', fontStyle: 'italic', mb: 0.5 }}>
              Forwarded from {message.forwarded_from.sender_name}
            </Typography>
          )}

          {message.reply_to_message_id && (
            <Box
              role="button"
              tabIndex={0}
              aria-label="Jump to quoted message"
              onClick={handleReplyPreviewClick}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleReplyPreviewClick(e as unknown as React.MouseEvent);
                }
              }}
              sx={{
                borderLeft: '3px solid #0f9d58',
                pl: 1,
                mb: 0.75,
                opacity: 0.85,
                cursor: onReplyPreviewClick ? 'pointer' : 'default',
                borderRadius: '4px 0 0 4px',
                mx: -0.5,
                px: 0.5,
                transition: 'background-color 0.15s',
                '&:hover': onReplyPreviewClick
                  ? { backgroundColor: 'rgba(15, 157, 88, 0.08)' }
                  : undefined,
              }}
            >
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#0f9d58' }}>
                {replyToMessage?.sender_name_cached ?? 'Member'}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: '#5f6368',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 220,
                }}
              >
                {replyPreviewText(replyToMessage)}
              </Typography>
            </Box>
          )}

          <Box
            onClick={onClick}
            sx={{ cursor: onClick ? 'pointer' : 'default' }}
          >
            {renderMessageBody()}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
              {message.is_pinned && (
                <PushPinIcon sx={{ fontSize: 12, color: '#9aa0a6', transform: 'rotate(45deg)' }} />
              )}
              {message._failed && (
                <Typography sx={{ fontSize: '0.65rem', color: '#d32f2f', maxWidth: 220 }}>
                  {message._error || 'Failed to send'}
                </Typography>
              )}
              <Typography sx={{ fontSize: '0.65rem', color: '#9aa0a6' }}>
                {formatMessageTime(message.timestamp)}
              </Typography>
              {message._optimistic && !message._failed && (
                <CircularProgress size={8} sx={{ color: '#9aa0a6' }} />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
